import { useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3, FileUp, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { EmptyState, PageHeader, Panel, QueryState, SourceBadge } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import AnalyticsImport from "@/components/AnalyticsImport";
import { CountryComparison } from "@/components/CountryComparison";

const tip = { contentStyle: { background: "#0A1024", border: "1px solid #243153", borderRadius: 10, color: "#F7FAFF", fontSize: 12 } };
const axis = { stroke: "#5D6B8C", fontSize: 11, tickLine: false, axisLine: false };
const METRICS = [["impressions", "Impressions"], ["reach", "Reach"], ["clicks", "Clicks"], ["engagements", "Engagements"], ["video_views", "Video views"],
  ["sessions", "Sessions"], ["users", "Users"], ["leads", "Leads"], ["qualified_leads", "Qualified leads"], ["conversions", "Conversions"], ["spend", "Spend"],
  ["assessment_starts", "Health Check starts"], ["assessment_completions", "Health Check completions"], ["trials", "Trials / accounts"], ["demos", "Demos requested"], ["customers", "Paid customers"]];
const SOURCE_TABS = [["live", "Live connected"], ["imported", "Manually imported"], ["demo", "Demonstration"]];

function summarise(rows) {
  const present = METRICS.filter(([k]) => rows.some((r) => r[k] !== undefined && r[k] !== null));
  const totals = Object.fromEntries(present.map(([k]) => [k, rows.reduce((s, r) => s + (r[k] || 0), 0)]));
  const trend = ["qualified_leads", "leads", "conversions", "clicks", "sessions", "impressions"].find((k) => k in totals);
  const has = (r) => r[trend] !== undefined && r[trend] !== null;
  const multiYear = new Set(rows.map((r) => r.date.slice(0, 4))).size > 1;
  const d = {}; const c = {};
  rows.filter(has).forEach((r) => {
    d[r.date] = d[r.date] || { key: r.date, date: multiYear ? r.date : r.date.slice(5), value: 0 }; d[r.date].value += r[trend];
    c[r.channel] = c[r.channel] || { channel: r.channel, value: 0 }; c[r.channel].value += r[trend];
  });
  return { present, totals, trend, daily: Object.values(d).sort((a, b) => (a.key < b.key ? -1 : 1)), byChannel: Object.values(c) };
}

function ImportHistory() {
  const { path, can, refresh } = useWorkspace();
  const q = useWsQuery("/analytics/imports");
  const [del, setDel] = useState(null);
  if (!q.data?.length) return null;
  const remove = async () => {
    try { const { data } = await api.post(path(`/analytics/imports/${del.id}/remove`), { confirm: true }); toast.success(`Removed ${data.rows_removed} imported rows`); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Panel title="Import history" eyebrow="Audit" testId="analytics-import-history">
      <div className="divide-y divide-[#172342]">{q.data.map((i) => (
        <div key={i.id} data-testid={`analytics-import-${i.id}`} className="py-2.5 flex flex-wrap items-center gap-3 text-xs">
          <span className="text-[#F7FAFF] font-medium">{i.filename}</span>
          <span className="text-[#7C8BAD]">{i.channel} · {i.date_from} to {i.date_to} · {i.rows_imported} rows ({i.rows_rejected} rejected) · {i.imported_by_name} · {fmtDateTime(i.created_at)}</span>
          {i.status === "removed" ? <span className="text-[#7C8BAD]">Removed by {i.removed_by_name}</span>
            : can("import_analytics") && <button data-testid={`analytics-import-remove-${i.id}`} onClick={() => setDel(i)} className="ml-auto cq-btn cq-btn-ghost !p-1.5" title="Remove import"><Trash2 size={13} /></button>}
        </div>))}</div>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} danger title="Remove this import?" confirmLabel="Remove rows"
        description="All rows from this file are deleted from Analytics. The import record and audit log entry stay." onConfirm={remove} />
    </Panel>
  );
}

function ImportedRows({ rows }) {
  return (
    <Panel title="Imported records" eyebrow="Original values preserved" testId="analytics-imported-rows">
      <div className="overflow-x-auto max-h-80"><table className="w-full text-xs">
        <thead><tr className="text-left text-[#7C8BAD]"><th className="py-1 pr-3 font-normal">Date</th><th className="pr-3 font-normal">Source</th><th className="pr-3 font-normal">Campaign</th><th className="pr-3 font-normal">Item</th><th className="font-normal">Metrics (source column = original value)</th></tr></thead>
        <tbody>{rows.slice(-100).reverse().map((r) => (
          <tr key={r.id} className="border-t border-[#172342] text-[#D5DDEB] align-top">
            <td className="py-1.5 pr-3 whitespace-nowrap">{r.date}</td><td className="pr-3">{r.channel}</td><td className="pr-3 max-w-[180px] truncate">{r.campaign_name || "—"}</td><td className="pr-3 max-w-[160px] truncate">{r.item_name || "—"}</td>
            <td className="text-[#AAB6CC]">{Object.entries(r.source_metrics || {}).map(([k, col]) => `${col} = ${r.source_values?.[col]}`).join(" · ")}</td>
          </tr>))}</tbody></table></div>
    </Panel>
  );
}

export default function Analytics() {
  const { can } = useWorkspace();
  const [source, setSource] = useState(null);
  const [imp, setImp] = useState(false);
  const q = useWsQuery(`/analytics${source ? `?source=${source}` : ""}`);
  const rows = useMemo(() => q.data?.rows || [], [q.data]);
  const s = useMemo(() => summarise(rows), [rows]);
  const active = q.data?.source;
  const label = Object.fromEntries(METRICS)[s.trend] || "";
  return (
    <div>
      <PageHeader eyebrow="Measure" title="Analytics" description="Each data source is shown on its own: live connector data, manually imported platform reports and demonstration data are never combined."
        actions={can("import_analytics") && <button data-testid="analytics-import-button" onClick={() => setImp(true)} className="cq-btn cq-btn-primary"><FileUp size={14} /> Import CSV report</button>} />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="analytics-source-tabs">
        {SOURCE_TABS.map(([k, l]) => (
          <button key={k} data-testid={`analytics-source-${k}`} disabled={!q.data?.counts?.[k]} onClick={() => setSource(k)}
            className={`px-3 py-1.5 rounded-lg text-xs border disabled:opacity-40 ${active === k ? "border-[#22E6F2]/50 text-[#22E6F2] bg-[#22E6F2]/10" : "border-[#243153] text-[#AAB6CC]"}`}>
            {l} · {q.data?.counts?.[k] ?? 0}</button>))}
      </div>
      <QueryState q={q}>
        {() => !rows.length ? <EmptyState icon={BarChart3} title="No analytics yet" description="Import a CSV report exported from Meta, Google Ads, LinkedIn or Google Analytics. Live connector data appears once a channel is connected and verified." /> : (
          <div className="space-y-6">
            <div className="flex items-center gap-2"><SourceBadge source={active} testId="analytics-source-badge" />{active === "demo" && <span data-testid="analytics-demo-badge" className="text-xs text-[#7C8BAD]">Seeded sample figures, not real performance.</span>}</div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="analytics-totals">
              {s.present.map(([k, l]) => (
                <div key={k} data-testid={`analytics-total-${k}`} className="cq-card p-5"><div className="text-xs text-[#AAB6CC]">{l}</div><div className="font-display text-2xl font-semibold text-[#F7FAFF] mt-1 tabular-nums">{Math.round(s.totals[k]).toLocaleString()}</div></div>))}
            </div>
            <p className="text-[11px] text-[#7C8BAD]">Only metrics present in this source are shown; nothing is estimated or filled in.</p>
            <Panel title={`${label} per day`} eyebrow="Trend" testId="analytics-leads-chart">
              <div className="h-72"><ResponsiveContainer><AreaChart data={s.daily}>
                <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22E6F2" stopOpacity={0.35} /><stop offset="100%" stopColor="#22E6F2" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#172342" vertical={false} /><XAxis dataKey="date" {...axis} /><YAxis {...axis} /><Tooltip {...tip} />
                <Area type="monotone" dataKey="value" name={label} stroke="#22E6F2" fill="url(#ga)" strokeWidth={2} />
              </AreaChart></ResponsiveContainer></div>
            </Panel>
            <Panel title={`${label} by channel`} eyebrow="Mix" testId="analytics-channel-chart">
              <div className="h-64"><ResponsiveContainer><BarChart data={s.byChannel}>
                <CartesianGrid stroke="#172342" vertical={false} /><XAxis dataKey="channel" {...axis} /><YAxis {...axis} /><Tooltip {...tip} cursor={{ fill: "#131F42" }} />
                <Bar dataKey="value" name={label} fill="#2962FF" radius={[6, 6, 0, 0]} />
              </BarChart></ResponsiveContainer></div>
            </Panel>
            {active === "imported" && <ImportedRows rows={rows} />}
          </div>)}
      </QueryState>
      <div className="mt-6"><CountryComparison source={["live", "imported"].includes(source) ? source : null} /></div>
      <div className="mt-6"><ImportHistory /></div>
      {imp && <AnalyticsImport open={imp} onOpenChange={setImp} />}
    </div>
  );
}
